import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { X, ChevronDown } from 'lucide-react';
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { useToast } from "../ui/use-toast";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useUTMTracking } from "../../hooks/useUTMTracking";
import { RTLInput, RTLTextarea } from "../ui/rtl-input";
import { isRTL, getTextDirection, getRTLClasses, getDirAttribute } from "../../utils/rtl";

// Form step types
type FormStep = "basic-info" | "contact" | "interest" | "experience" | "success";

// Country code type
interface CountryCode {
  code: string;
  country: string;
  flag: string;
  popular?: boolean;
}

// Form data schema
const formSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.object({
    countryCode: z.string(),
    number: z.string().min(6, "Phone number is too short").max(15, "Phone number is too long")
  }),
  interest: z.string(),
  previousPurchases: z.string(),
  investmentTimeline: z.string()
});

type FormData = z.infer<typeof formSchema>;

// All country codes data
const allCountryCodes: CountryCode[] = [
  { code: "+971", country: "United Arab Emirates", flag: "🇦🇪", popular: true },
  { code: "+91", country: "India", flag: "🇮🇳", popular: true },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧", popular: true },
  { code: "+1", country: "United States", flag: "🇺🇸", popular: true },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦", popular: true },
  { code: "+974", country: "Qatar", flag: "🇶🇦", popular: true },
  { code: "+965", country: "Kuwait", flag: "🇰🇼", popular: true },
  { code: "+968", country: "Oman", flag: "🇴🇲", popular: true },
  { code: "+973", country: "Bahrain", flag: "🇧🇭", popular: true },
  { code: "+20", country: "Egypt", flag: "🇪🇬" },
  { code: "+962", country: "Jordan", flag: "🇯🇴" },
  { code: "+961", country: "Lebanon", flag: "🇱🇧" },
  { code: "+963", country: "Syria", flag: "🇸🇾" },
  { code: "+964", country: "Iraq", flag: "🇮🇶" },
  { code: "+98", country: "Iran", flag: "🇮🇷" },
  { code: "+90", country: "Turkey", flag: "🇹🇷" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+93", country: "Afghanistan", flag: "🇦🇫" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
  { code: "+95", country: "Myanmar", flag: "🇲🇲" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+852", country: "Hong Kong", flag: "🇭🇰" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+84", country: "Vietnam", flag: "🇻🇳" },
  { code: "+66", country: "Thailand", flag: "🇹🇭" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩" },
  { code: "+63", country: "Philippines", flag: "🇵🇭" },
  { code: "+7", country: "Russia", flag: "🇷🇺" },
  { code: "+380", country: "Ukraine", flag: "🇺🇦" },
  { code: "+48", country: "Poland", flag: "🇵🇱" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+34", country: "Spain", flag: "🇪🇸" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+31", country: "Netherlands", flag: "🇳🇱" },
  { code: "+32", country: "Belgium", flag: "🇧🇪" },
  { code: "+46", country: "Sweden", flag: "🇸🇪" },
  { code: "+47", country: "Norway", flag: "🇳🇴" },
  { code: "+45", country: "Denmark", flag: "🇩🇰" },
  { code: "+358", country: "Finland", flag: "🇫🇮" },
  { code: "+43", country: "Austria", flag: "🇦🇹" },
  { code: "+41", country: "Switzerland", flag: "🇨🇭" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+30", country: "Greece", flag: "🇬🇷" },
  { code: "+353", country: "Ireland", flag: "🇮🇪" },
  { code: "+972", country: "Israel", flag: "🇮🇱" }
];

interface DynamicDownloadFormProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: Id<"projects">;
  language: string;
  content?: {
    title?: string;
    subtitle?: string;
    
    // Step titles and descriptions
    stepTitles?: {
      basicInfo?: string;
      contact?: string;
      interest?: string;
      experience?: string;
    };
    
    stepDescriptions?: {
      basicInfo?: string;
      contact?: string;
      interest?: string;
      experience?: string;
    };
    
    // Form labels
    labels?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      interest?: string;
      previousPurchases?: string;
      investmentTimeline?: string;
      continue?: string;
      back?: string;
      submit?: string;
      submitting?: string;
      thankYou?: string;
      willContactYou?: string;
    };
    
    // Validation messages
    validationMessages?: {
      firstNameRequired?: string;
      lastNameRequired?: string;
      emailRequired?: string;
      emailInvalid?: string;
      phoneRequired?: string;
    };
    
    // Options
    interestOptions?: { value: string; label: string }[];
    previousPurchasesOptions?: { value: string; label: string }[];
    investmentTimelineOptions?: { value: string; label: string }[];
    success?: {
      title?: string;
      description?: string;
    };
    error?: {
      title?: string;
      description?: string;
    };
    privacyText?: string;
  };
  onSuccess?: () => void;
}

// Animation variants - only for step transitions
const stepVariants = {
  hidden: { 
    opacity: 0,
    x: -20,
    scale: 0.95
  },
  visible: { 
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0,
    x: 20,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }
};

export const DynamicDownloadForm: React.FC<DynamicDownloadFormProps> = ({
  isOpen,
  onClose,
  projectId,
  language,
  content,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState<FormStep>("basic-info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, setValue, formState: { errors }, reset, getValues } = useForm<FormData>();
  const [selectedCountry, setSelectedCountry] = useState("+971");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const createLead = useMutation(api.leads.create);
  const linkLeadToSession = useMutation(api.visitorTracking.linkLeadToSession);
  
  // RTL detection
  const isCurrentLanguageRTL = isRTL(language);
  const textDirection = getTextDirection(language);
  const rtlClasses = getRTLClasses(language);
  const dirAttribute = getDirAttribute(language);
  
  // Use UTM tracking hook
  const { getLeadTrackingData } = useUTMTracking(projectId);
  
  // Anti-spam states
  const [formStartTime] = useState<number>(Date.now());
  const [honeypot, setHoneypot] = useState<string>('');

  // Default content with fallbacks - memoized
  const defaultContent = useMemo(() => ({
    title: content?.title || "Download Investment Brochure",
    subtitle: content?.subtitle || "Get exclusive property details and investment insights",
    
    // Step titles
    stepTitles: {
      basicInfo: content?.stepTitles?.basicInfo || "Personal Information",
      contact: content?.stepTitles?.contact || "Contact Information",
      interest: content?.stepTitles?.interest || "Your Interest",
      experience: content?.stepTitles?.experience || "Your Experience"
    },
    
    // Step descriptions
    stepDescriptions: {
      basicInfo: content?.stepDescriptions?.basicInfo || "Let's start with your basic details",
      contact: content?.stepDescriptions?.contact || "How can we reach you?",
      interest: content?.stepDescriptions?.interest || "What interests you most about this property?",
      experience: content?.stepDescriptions?.experience || "Help us understand your background"
    },
    
    labels: {
      firstName: content?.labels?.firstName || "First Name",
      lastName: content?.labels?.lastName || "Last Name",
      email: content?.labels?.email || "Email Address",
      phone: content?.labels?.phone || "Phone Number",
      interest: content?.labels?.interest || "What interests you most?",
      previousPurchases: content?.labels?.previousPurchases || "Previous property purchases",
      investmentTimeline: content?.labels?.investmentTimeline || "Investment timeline",
      continue: content?.labels?.continue || "Continue",
      back: content?.labels?.back || "Back",
      submit: content?.labels?.submit || "Download Brochure",
      submitting: content?.labels?.submitting || "Submitting...",
      thankYou: content?.labels?.thankYou || "Thank You!",
      willContactYou: content?.labels?.willContactYou || "Your brochure request has been received. We'll send it to your email shortly.",
      ...content?.labels
    },
    
    // Validation messages
    validationMessages: {
      firstNameRequired: content?.validationMessages?.firstNameRequired || "First name is required",
      lastNameRequired: content?.validationMessages?.lastNameRequired || "Last name is required",
      emailRequired: content?.validationMessages?.emailRequired || "Please enter your email address",
      emailInvalid: content?.validationMessages?.emailInvalid || "Please enter a valid email address",
      phoneRequired: content?.validationMessages?.phoneRequired || "Please enter a valid phone number"
    },
    
    interestOptions: content?.interestOptions || [
      { value: 'investment', label: 'Investment opportunities' },
      { value: 'end-user', label: 'Personal residence' },
      { value: 'agent', label: 'Real estate agent' },
      { value: 'exploring', label: 'Just exploring' }
    ],
    previousPurchasesOptions: content?.previousPurchasesOptions || [
      { value: 'none', label: 'None' },
      { value: '1-2', label: '1-2 properties' },
      { value: '3-5', label: '3-5 properties' },
      { value: '5+', label: '5+ properties' }
    ],
    investmentTimelineOptions: content?.investmentTimelineOptions || [
      { value: 'immediate', label: 'Immediately' },
      { value: '3months', label: 'Within 3 months' },
      { value: '6months', label: 'Within 6 months' },
      { value: 'exploring', label: 'Just exploring' }
    ],
    success: {
      title: content?.success?.title || "Thank You!",
      description: content?.success?.description || "Your brochure request has been received. We'll send it to your email shortly."
    },
    error: {
      title: content?.error?.title || "Submission Error",
      description: content?.error?.description || "Failed to submit the request. Please try again later."
    },
    privacyText: content?.privacyText || "By submitting this form, you agree to our privacy policy and consent to being contacted regarding your interest in our properties."
  }), [content]);

  // Progress calculation
  const getProgress = useCallback(() => {
    const steps: FormStep[] = ["basic-info", "contact", "interest", "experience"];
    return ((steps.indexOf(currentStep) + 1) / steps.length) * 100;
  }, [currentStep]);

  // Filter countries based on search - memoized
  const filteredCountries = useMemo(() => 
    allCountryCodes.filter(country => 
      country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.includes(searchQuery)
    ), [searchQuery]
  );

  // Group countries by popular and others - memoized
  const { popularCountries, otherCountries } = useMemo(() => ({
    popularCountries: filteredCountries.filter(c => c.popular),
    otherCountries: filteredCountries.filter(c => !c.popular)
  }), [filteredCountries]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format phone number as user types - memoized
  const formatPhoneNumber = useCallback((value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
  }, []);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    e.target.value = formatted;
  }, [formatPhoneNumber]);

  // Validation functions - memoized to prevent re-renders
  const validateStep = useCallback((step: FormStep): boolean => {
    const values = getValues();

    if (step === "basic-info") {
      return !!(values.firstName && values.firstName.length >= 2 && values.lastName && values.lastName.length >= 2);
    } else if (step === "contact") {
      return !!(values.email && /\S+@\S+\.\S+/.test(values.email) && values.phone?.number && values.phone.number.replace(/\D/g, '').length >= 6);
    } else if (step === "interest") {
      return !!values.interest;
    }
    return true;
  }, [getValues]);

  const handleNext = useCallback(() => {
    if (validateStep(currentStep)) {
      const steps: FormStep[] = ["basic-info", "contact", "interest", "experience"];
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex < steps.length - 1) {
        setCurrentStep(steps[currentIndex + 1]);
      }
    }
  }, [currentStep, validateStep]);

  const handlePrevious = useCallback(() => {
    const steps: FormStep[] = ["basic-info", "contact", "interest", "experience"];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  }, [currentStep]);

  const handleSubmitForm = useCallback(async () => {
    if (!validateStep("experience") || isSubmitting) return;

    setIsSubmitting(true);
    const formData = getValues();
    
    // GTM Tracking - Brochure Download Event
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'file_download',
        'lead_type': 'brochure_download',
        'file_name': `Property_${projectId}_Brochure.pdf`,
        'property_id': projectId,
        'file_type': 'pdf',
        'lead_value': 50,
        'currency': 'AED',
        'user_email': formData.email,
        'user_phone': formData.phone?.number,
        'form_type': 'download_form',
        'lead_source': 'website',
        'page_location': window.location.href,
        'user_interest': formData.interest,
        'user_experience': formData.previousPurchases,
        'user_timeline': formData.investmentTimeline
      });
    }
    
    try {
      // Get user's IP address
      const ipAddress = await fetch('https://api.ipify.org?format=json')
        .then(res => res.json())
        .then(data => data.ip)
        .catch(() => null);

      // Get tracking data from the UTM hook
      const trackingData = getLeadTrackingData();

      // Submit lead to database with anti-spam measures and tracking data
      const result = await createLead({
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: {
          countryCode: selectedCountry,
          number: formData.phone?.number || ''
        },
        projectId,
        language,
        purpose: formData.interest === 'investment' ? 'investment' : 'end-user',
        budget: formData.previousPurchases, // Map previousPurchases to budget field for download form
        timeline: formData.investmentTimeline, // Map investmentTimeline to timeline field
        contactPreference: 'email', // Default for brochure downloads
        formType: 'download_form', // Identify this as download form
        ...trackingData, // Include all UTM and visitor tracking data
        ipAddress: ipAddress || undefined,
        userAgent: navigator.userAgent,
        // Anti-spam fields
        honeypot: honeypot,
        formStartTime: formStartTime,
        formSubmitTime: Date.now(),
      });

      if (result.success) {
        // Link the lead to the visitor session if we have tracking data
        if (trackingData.visitorId && trackingData.sessionId) {
          try {
            await linkLeadToSession({
              sessionId: trackingData.sessionId,
              visitorId: trackingData.visitorId,
              leadId: result.leadId,
            });
          } catch (error) {
    
          }
        }
        
        toast({
          title: defaultContent.success.title,
          description: defaultContent.success.description,
          variant: "default"
        });
        
        setCurrentStep("success");
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
        
        // Reset form after delay
        setTimeout(() => {
          reset();
          setCurrentStep("basic-info");
          onClose();
        }, 3000);
      } else {
        throw new Error("Failed to submit");
      }
    } catch (error) {

      toast({
        title: defaultContent.error.title,
        description: defaultContent.error.description,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [validateStep, isSubmitting, getValues, selectedCountry, projectId, language, honeypot, formStartTime, createLead, defaultContent, toast, onSuccess, reset, onClose, getLeadTrackingData, linkLeadToSession]);

  // Country selection handlers
  const handleCountrySelect = useCallback((countryCode: string) => {
    setSelectedCountry(countryCode);
    setIsCountryDropdownOpen(false);
    setSearchQuery("");
  }, []);

  const handleCountrySearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSearchQuery(e.target.value);
  }, []);

  // Memoized country search input to prevent focus loss
  const CountrySearchInput = React.memo(() => (
    <RTLInput
      type="text"
      language={language}
      placeholder="Search countries..."
      value={searchQuery}
      onChange={handleCountrySearch}
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
    />
  ));

  // Memoized country button to prevent re-renders
  const CountryButton = React.memo(({ country, isSelected, onClick }: {
    country: CountryCode;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-2 py-1.5 text-sm rounded-md ${
        isSelected
          ? 'bg-primary/5 text-primary'
          : 'hover:bg-gray-50'
      }`}
    >
      <span className="text-lg">{country.flag}</span>
      <span className="flex-1 text-sm">{country.country}</span>
      <span className="text-gray-500 text-xs">{country.code}</span>
    </button>
  ));

  // Selection handlers for options
  const handleInterestSelect = useCallback((value: string) => {
    setValue('interest', value);
  }, [setValue]);

  const handlePreviousPurchasesSelect = useCallback((value: string) => {
    setValue('previousPurchases', value);
  }, [setValue]);

  const handleInvestmentTimelineSelect = useCallback((value: string) => {
    setValue('investmentTimeline', value);
  }, [setValue]);

  // Progress bar component
  const ProgressBar = React.memo(() => (
    <motion.div 
      className="h-1 bg-gray-200 rounded-full overflow-hidden"
      initial={false}
    >
      <motion.div
        className="h-full bg-primary"
        initial={{ width: 0 }}
        animate={{ width: `${getProgress()}%` }}
        transition={{ duration: 0.5 }}
      />
    </motion.div>
  ));

  // Basic Info step
  const BasicInfoStep = React.memo(() => (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        <h3 className="text-xl font-bold text-gray-900">{defaultContent.stepTitles.basicInfo}</h3>
        <p className="text-gray-600">{defaultContent.stepDescriptions.basicInfo}</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            {defaultContent.labels.firstName}*
          </label>
          <RTLInput
            type="text"
            id="firstName"
            language={language}
            {...register('firstName', { required: true, minLength: 2 })}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.firstName ? 'border-red-500' : 'border-gray-300'
            } shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
            placeholder="Enter first name"
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{defaultContent.validationMessages.firstNameRequired}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            {defaultContent.labels.lastName}*
          </label>
          <RTLInput
            type="text"
            id="lastName"
            language={language}
            {...register('lastName', { required: true, minLength: 2 })}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.lastName ? 'border-red-500' : 'border-gray-300'
            } shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
            placeholder="Enter last name"
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{defaultContent.validationMessages.lastNameRequired}</p>
          )}
        </div>
      </div>
    </div>
  ));

  // Contact step
  const ContactStep = React.memo(() => (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        <h3 className="text-xl font-bold text-gray-900">{defaultContent.stepTitles.contact}</h3>
        <p className="text-gray-600">{defaultContent.stepDescriptions.contact}</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            {defaultContent.labels.email}*
          </label>
          <RTLInput
            type="email"
            id="email"
            language={language}
            {...register('email', { 
              required: true, 
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            })}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            } shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
            placeholder="Enter your email"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{defaultContent.validationMessages.emailInvalid}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            {defaultContent.labels.phone}*
          </label>
          <div className="flex gap-2">
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                className="flex items-center gap-2 w-[110px] px-2 py-3 rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-white"
              >
                <span className="text-lg">
                  {allCountryCodes.find(c => c.code === selectedCountry)?.flag}
                </span>
                <span className="flex-1 text-left text-sm font-medium truncate">
                  {selectedCountry}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Country Dropdown */}
              <AnimatePresence>
                {isCountryDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute z-50 mt-1 w-[260px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                  >
                    <div className="p-2">
                      <CountrySearchInput />
                    </div>

                    <div className="max-h-[280px] overflow-y-auto">
                      {/* Popular Countries */}
                      {popularCountries.length > 0 && (
                        <div className="px-2 py-1.5">
                          <div className="text-xs font-medium text-gray-500 px-2 mb-1">Popular</div>
                          {popularCountries.map((country) => (
                            <CountryButton key={country.code} country={country} isSelected={selectedCountry === country.code} onClick={() => handleCountrySelect(country.code)} />
                          ))}
                        </div>
                      )}

                      {/* Other Countries */}
                      {otherCountries.length > 0 && (
                        <div className="px-2 py-1.5">
                          {popularCountries.length > 0 && (
                            <div className="text-xs font-medium text-gray-500 px-2 mb-1">Other Countries</div>
                          )}
                          {otherCountries.map((country) => (
                            <CountryButton key={country.code} country={country} isSelected={selectedCountry === country.code} onClick={() => handleCountrySelect(country.code)} />
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <RTLInput
              type="tel"
              id="phone"
              language={language}
              {...register('phone.number', { 
                required: true,
                minLength: 6,
                maxLength: 15,
                pattern: /^[0-9\s]+$/
              })}
              onChange={handlePhoneChange}
              className={`flex-1 px-4 py-3 rounded-lg border ${
                errors.phone?.number ? 'border-red-500' : 'border-gray-300'
              } shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors`}
              placeholder="Enter phone number"
            />
          </div>
          {errors.phone?.number && (
            <p className="mt-1 text-sm text-red-600">{defaultContent.validationMessages.phoneRequired}</p>
          )}
        </div>
      </div>
    </div>
  ));

  // Interest step
  const InterestStep = React.memo(() => {
    const [selectedInterest, setSelectedInterest] = useState(getValues('interest') || '');
    
    const handleSelect = useCallback((value: string) => {
      setSelectedInterest(value);
      handleInterestSelect(value);
    }, [handleInterestSelect]);
    
    return (
      <div className="space-y-6">
        <div className="text-center space-y-3">
          <h3 className="text-xl font-bold text-gray-900">{defaultContent.stepTitles.interest}</h3>
          <p className="text-gray-600">{defaultContent.stepDescriptions.interest}</p>
        </div>

        <div className="space-y-3">
          {defaultContent.interestOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                selectedInterest === option.value
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-gray-200 hover:border-primary/50'
              }`}
            >
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                  selectedInterest === option.value ? 'border-primary bg-primary' : 'border-gray-300'
                }`}>
                  {selectedInterest === option.value && (
                    <div className="w-2 h-2 bg-white rounded-full m-0.5" />
                  )}
                </div>
                <span className="font-medium">{option.label}</span>
              </div>
            </button>
          ))}
          {errors.interest && (
            <p className="mt-1 text-sm text-red-600">{errors.interest.message || 'Please select an option'}</p>
          )}
        </div>
      </div>
    );
  });

  // Experience step
  const ExperienceStep = React.memo(() => {
    const [selectedPreviousPurchases, setSelectedPreviousPurchases] = useState(getValues('previousPurchases') || '');
    const [selectedInvestmentTimeline, setSelectedInvestmentTimeline] = useState(getValues('investmentTimeline') || '');
    
    const handlePreviousPurchasesChange = useCallback((value: string) => {
      setSelectedPreviousPurchases(value);
      handlePreviousPurchasesSelect(value);
    }, [handlePreviousPurchasesSelect]);
    
    const handleInvestmentTimelineChange = useCallback((value: string) => {
      setSelectedInvestmentTimeline(value);
      handleInvestmentTimelineSelect(value);
    }, [handleInvestmentTimelineSelect]);
    
    return (
      <div className="space-y-6">
        <div className="text-center space-y-3">
          <h3 className="text-xl font-bold text-gray-900">{defaultContent.stepTitles.experience}</h3>
          <p className="text-gray-600">{defaultContent.stepDescriptions.experience}</p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {defaultContent.labels.previousPurchases}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {defaultContent.previousPurchasesOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handlePreviousPurchasesChange(option.value)}
                  className={`p-3 rounded-lg border-2 transition-colors text-center ${
                    selectedPreviousPurchases === option.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {defaultContent.labels.investmentTimeline}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {defaultContent.investmentTimelineOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleInvestmentTimelineChange(option.value)}
                  className={`p-3 rounded-lg border-2 transition-colors text-center ${
                    selectedInvestmentTimeline === option.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium text-sm">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
            <p>{defaultContent.privacyText}</p>
          </div>
        </div>
      </div>
    );
  });

  // Success step
  const SuccessStep = React.memo(() => (
    <div className="text-center py-8">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="w-16 h-16 mx-auto mb-6 bg-green-100 text-green-500 rounded-full flex items-center justify-center"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      </motion.div>
      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-semibold mb-4"
      >
        {defaultContent.success.title}
      </motion.h3>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-gray-600 mb-4"
      >
        {defaultContent.success.description}
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4"
      >
        <div className="flex items-center justify-center mb-2">
          <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-blue-800 font-medium">Brochure Email Sent!</span>
        </div>
        <p className="text-blue-700 text-sm">
          Check your email inbox for the brochure download link.
        </p>
      </motion.div>
    </div>
  ));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-black/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div 
            className={`relative w-full max-w-md p-6 m-4 bg-white rounded-xl shadow-xl ${rtlClasses}`}
            dir={dirAttribute}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Honeypot field - hidden from users but visible to bots */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                pointerEvents: 'none'
              }}
              tabIndex={-1}
              autoComplete="off"
            />

            <button 
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={onClose}
              aria-label="Close modal"
            >
              <X size={24} />
            </button>

            {currentStep !== "success" && (
              <>
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-gray-800">{defaultContent.title}</h2>
                  <p className="mt-2 text-gray-600">{defaultContent.subtitle}</p>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <ProgressBar />
                </div>
              </>
            )}

            <AnimatePresence mode="wait">
              {currentStep === "basic-info" && (
                <motion.div key="basic-info" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
                  <BasicInfoStep />
                </motion.div>
              )}
              {currentStep === "contact" && (
                <motion.div key="contact" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
                  <ContactStep />
                </motion.div>
              )}
              {currentStep === "interest" && (
                <motion.div key="interest" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
                  <InterestStep />
                </motion.div>
              )}
              {currentStep === "experience" && (
                <motion.div key="experience" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
                  <ExperienceStep />
                </motion.div>
              )}
              {currentStep === "success" && (
                <motion.div key="success" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
                  <SuccessStep />
                </motion.div>
              )}
            </AnimatePresence>

            {currentStep !== "success" && (
              <div className="mt-8 flex justify-between">
                {currentStep !== "basic-info" ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    className="px-4 py-2"
                    disabled={isSubmitting}
                  >
                    {defaultContent.labels.back}
                  </Button>
                ) : (
                  <div></div>
                )}

                {currentStep !== "experience" ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-3 bg-primary hover:bg-primary/90 text-white"
                    disabled={isSubmitting}
                  >
                    {defaultContent.labels.continue}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit(handleSubmitForm)}
                    className="px-6 py-3 bg-primary hover:bg-primary/90 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? defaultContent.labels.submitting : defaultContent.labels.submit}
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 